import useAnalyticsCalls from '@/hooks/use-analytics';
import { useFunnelLead } from '@/hooks/useFunnelLead';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JoinFunnelLayout } from '../JoinFunnelLayout';
import { FunnelField } from '../components/FunnelField';
import { ValuePropCard } from '../components/ValuePropCard';
import { FUNNEL_COPY } from '../funnel-copy';
import { stepRoutePath, useFunnelVariant } from '../routes';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export function JoinStep1() {
  const variant = useFunnelVariant();
  const copy = FUNNEL_COPY[variant].step1;
  const navigate = useNavigate();
  const { lead, updateLead } = useFunnelLead();
  const analytics = useAnalyticsCalls();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Hydrate from server lead exactly once when it lands.
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (!lead || hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    setFirstName(lead.firstName ?? '');
    setLastName(lead.lastName ?? '');
    setEmail(lead.email ?? '');
  }, [lead]);

  // Onboarding - Started (Meta view-002 per Tracking Requirements) fires on
  // first mount only. The per-step page view is handled globally by
  // useAnalyticsPageViews, which now attaches `positioning_variant` and
  // `funnel_step` to `[Amplitude] Page Viewed` for /join routes.
  const hasFiredOnceRef = useRef(false);
  useEffect(() => {
    if (hasFiredOnceRef.current) return;
    hasFiredOnceRef.current = true;
    analytics.callOnboardingStarted();
  }, [analytics]);

  const handleFieldBlur = async (field: 'first_name' | 'last_name' | 'email', value: string) => {
    if (!value) return;
    if (field === 'email' && !EMAIL_REGEX.test(value)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(null);
    try {
      await updateLead(
        field === 'first_name' ? { firstName: value } : field === 'last_name' ? { lastName: value } : { email: value },
      );
      analytics.callFunnelFieldCaptured({ field, value, positioningVariant: variant });
    } catch {
      // Best-effort; FunnelGuard surfaces the failure and lazy-mint retries.
    }
  };

  const isComplete = firstName.trim() && lastName.trim() && email.trim() && !emailError;

  const handleContinue = () => {
    if (!isComplete) return;
    navigate(stepRoutePath(variant, 2));
  };

  return (
    <JoinFunnelLayout step={1} onContinue={handleContinue} isContinueDisabled={!isComplete}>
      <div className="flex flex-col gap-3">
        <h1 className="text-catch-dark-blue-90 text-3xl leading-tight font-bold">{copy.header}</h1>
        <p className="text-base text-gray-500">{copy.subheader}</p>
      </div>

      <div className="flex flex-col gap-4">
        <FunnelField
          id="first-name"
          label="Your name"
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onBlur={(e) => handleFieldBlur('first_name', e.target.value.trim())}
          autoComplete="given-name"
        />
        <FunnelField
          id="last-name"
          placeholder="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          onBlur={(e) => handleFieldBlur('last_name', e.target.value.trim())}
          autoComplete="family-name"
        />
        <FunnelField
          id="email"
          label="Email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          onBlur={(e) => handleFieldBlur('email', e.target.value.trim())}
          autoComplete="email"
          error={emailError}
        />
      </div>

      <ValuePropCard copy={copy.valueProp} />
    </JoinFunnelLayout>
  );
}

export default JoinStep1;
